import { isArray, isValidArray, isValidObject, catchError } from 'corp-front-vue-commons/src/utils/functions'
import store from '@/store'

class GMaps {
  constructor (config) {
    const {
      mapCenterCoordinates = null,
      originMarkerImage = null,
      destinytMarkerImage = null,
      gpsMarkerImage = null,
      drivertMarkerImage = null,
      clientMarkerImage = null
    } = config || {}

    this.map = null
    this.bounds = null
    this.mapCenterCoordinates = mapCenterCoordinates || { latitude: -21.0660556, longitude: -48.8053475 }
    this.geolocationPermission = false
    this.markersArray = []
    this.trackRideAdjustMap = 0
    this.originPosition = null
    this.destinyPosition = null
    this.autocomplete = null
    this.directionsService = null
    this.directionsDisplay = null
    this.originMarkerImage = originMarkerImage
    this.destinytMarkerImage = destinytMarkerImage
    this.gpsMarkerImage = gpsMarkerImage
    this.drivertMarkerImage = drivertMarkerImage
    this.clientMarkerImage = clientMarkerImage
    this.adjustMapZoom = true
  }

  appendScript () {
    const elem = document.getElementById('dgoogle')

    if (!elem) {
      const script = document.createElement('script')
      script.src = 'https://img0.didiglobal.com/static/map_webapp_ibt/mapsdk/google/1.0.0/DGoogle.min.js'
      script.id = 'dgoogle'
      document.body.appendChild(script)
    }
  }

  async getGoogle () {
    await window.DGoogle.getGoogle()
    if (window.google && typeof window.google === 'object') {
      console.log('Google Maps loaded')
    }
  }

  init () {
    this.appendScript()

    const DGoogleIsLoaded = setInterval(async () => {
      if (window.DGoogle && typeof window.DGoogle === 'object') {
        clearInterval(DGoogleIsLoaded)
        await this.getGoogle()
      }
    }, 500)
  }

  renderDefaultMap (mapSelector) {
    if (!window.google) return

    try {
      const { latitude, longitude } = this.mapCenterCoordinates
      const options = {
        mapTypeId: 'roadmap',
        center: new window.google.maps.LatLng(latitude, longitude),
        zoom: 6,
        disableDefaultUI: true
      }

      this.bounds = new window.google.maps.LatLngBounds()
      this.map = new window.google.maps.Map(mapSelector, options)
      this.map.setTilt(45)
    } catch (error) {
      throw new Error(error)
    }
  }

  removeDuplicateMarker () {
    const hasOriginPositionItem = this.markersArray.find(marker => marker.title === 'origin')
    const hasOriginPositionItems = this.markersArray.filter(marker => marker.title === 'origin')
    const hasDestinyPositionItem = this.markersArray.find(marker => marker.title === 'destiny')
    const hasDestinyPositionItems = this.markersArray.filter(marker => marker.title === 'destiny')

    if (hasOriginPositionItem && hasOriginPositionItems.length > 1) {
      hasOriginPositionItem.setMap(null)
      this.markersArray = this.markersArray.filter(marker => marker.markerId !== hasOriginPositionItem.markerId)
    }

    if (hasDestinyPositionItem && hasDestinyPositionItems.length > 1) {
      hasDestinyPositionItem.setMap(null)
      this.markersArray = this.markersArray.filter(marker => marker.markerId !== hasDestinyPositionItem.markerId)
    }
  }

  adjustBounds = function (adjustMarkerType, position) {
    if (
      (
        adjustMarkerType === 'originDestiny' ||
        adjustMarkerType === 'currentLocation' ||
        this.trackRideAdjustMap <= 1
      ) && this.bounds && this.map
    ) {
      this.bounds.extend(position)
      this.map.fitBounds(this.bounds)
    }

    if (adjustMarkerType === 'trackRide') this.trackRideAdjustMap++
  }

  addMarker = function ({ pos, draggable = true, adjustMarkerType = null }) {
    try {
      const position = new window.google.maps.LatLng(pos.latitude, pos.longitude)
      const map = this.map
      const title = pos.ref
      const icon = this.renderMarkerImage(adjustMarkerType, this.markersArray.length)

      const marker = new window.google.maps.Marker({
        markerId: this.markersArray.length,
        position,
        map,
        title,
        draggable: adjustMarkerType !== 'trackRide',
        icon
      })

      this.markersArray.push(marker)
      this.removeDuplicateMarker()
      this.adjustBounds(adjustMarkerType, position)

      window.google.maps.event.addListener(marker, 'dragend', async (event) => {
        await this.updateMarkerPosition(event, pos.ref)
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  renderMarkerImage (markerType, markersArrayLength) {
    if (markerType === 'originDestiny') {
      return markersArrayLength === 1 ? this.originMarkerImage : this.destinytMarkerImage
    }

    if (markerType === 'trackRide') {
      return markersArrayLength === 1 ? this.originMarkerImage : this.drivertMarkerImage
    }

    return this.gpsMarkerImage
  }

  async setZoom (number = 18) {
    this.map.setZoom(number)
  }

  async convertPositionToAddress (latlng) {
    try {
      const geocoder = new window.google.maps.Geocoder()
      const latlngStr = latlng.split(',', 2)
      const location = {
        lat: parseFloat(latlngStr[0]),
        lng: parseFloat(latlngStr[1])
      }

      return new Promise((resolve, reject) => {
        return geocoder.geocode({ location }, (results, status) => {
          if (status === 'OK') {
            const data = isValidArray(results) ? results[0] : {}
            return resolve(data)
          }

          return reject(status)
        })
      })
    } catch (error) {
      catchError(error)
    }
  }

  getCurrentPosition () {
    if (!navigator.geolocation) {
      throw new Error('Geolocation API not supported')
    }

    const cls = this
    const options = {
      timeout: 10000,
      enableHighAccuracy: true,
      maximumAge: 1000
    }

    return new Promise(function (resolve, reject) {
      return navigator.geolocation.getCurrentPosition(async ({ coords }) => {
        const { latitude, longitude } = coords
        const address = await cls.convertPositionToAddress(`${latitude},${longitude}`)
        const position = {
          ref: 'origin',
          address: address.formatted_address,
          number: cls.getStreetNumber(address),
          latitude,
          longitude
        }

        cls.originPosition = position
        cls.addMarker({
          pos: position,
          draggable: true,
          adjustMarkerType: 'currentLocation'
        })

        if (isArray(cls.markersArray) && cls.markersArray.length === 1) {
          cls.setZoom(18)
        }

        if (isArray(cls.markersArray) && cls.markersArray.length > 1) {
          cls.removePreviousRoutes()
          cls.directions()
        }

        cls.geolocationPermission = true

        // propagation event
        store.dispatch('webcallMoveMarkers/toggle', !store.state.webcallMoveMarkers.toggle)

        return resolve(position)
      }, (err) => {
        cls.geolocationPermission = false
        // eslint-disable-next-line prefer-promise-reject-errors
        return reject(`ERROR(${err.code}): ${err.message}`)
      }, options)
    })
  }

  getPositions () {
    return {
      originPosition: this.originPosition,
      destinyPosition: this.destinyPosition
    }
  }

  resetPositions () {
    this.originPosition = null
    this.destinyPosition = null
  }

  removeAllMarkers () {
    if (isValidArray(this.markersArray)) {
      this.markersArray.forEach(marker => marker.setMap(null))
      this.markersArray = []
    }
  }

  resetDirections () {
    this.directionsService = null
    this.removePreviousRoutes()
  }

  resetMapResources () {
    this.trackRideAdjustMap = 0
    this.adjustMapZoom = true
    this.resetPositions()
    this.removeAllMarkers()
    this.resetDirections()
  }

  resetMap (mapSelector) {
    this.resetMapResources()
    this.renderDefaultMap(mapSelector)

    if (this.geolocationPermission) {
      this.getCurrentPosition()
    }
  }

  getCoordinateValue = function (val) {
    return typeof val === 'function' ? val() : val
  }

  getStreetNumber (val) {
    const getNumber = (val) => {
      const regex = /,\s*/
      const match = val.split(regex)
      return match && match.length > 1 ? match[match.length - 1] : null
    }

    return val && val.name ? getNumber(val.name) : null
  }

  initAutocomplete = function (inputSelector, positionType) {
    this.autocomplete = new window.google.maps.places.Autocomplete(inputSelector)
    this.autocomplete.addListener('place_changed', () => {
      try {
        const place = this.autocomplete.getPlace()
        const positionData = isValidObject(place)
          ? {
            ref: positionType,
            address: place.formatted_address,
            number: this.getStreetNumber(place),
            latitude: this.getCoordinateValue(place.geometry.location.lat),
            longitude: this.getCoordinateValue(place.geometry.location.lng)
          } : null

        if (positionType === 'origin') this.originPosition = positionData
        if (positionType === 'destiny') this.destinyPosition = positionData

        store.dispatch('webcallMoveMarkers/toggle', !store.state.webcallMoveMarkers.toggle)
      } catch (error) {
        throw new Error(error)
      }
    })
  }

  removePreviousRoutes = function () {
    if (this.directionsDisplay) {
      this.directionsDisplay.setMap(null)
      this.directionsDisplay = null
    }
  }

  directions = function () {
    try {
      const { originPosition, destinyPosition } = this.getPositions()

      if (originPosition && destinyPosition) {
        const directionsConfig = {
          origin: `${originPosition.latitude}, ${originPosition.longitude}`,
          destination: `${destinyPosition.latitude}, ${destinyPosition.longitude}`,
          optimizeWaypoints: true,
          travelMode: window.google.maps.TravelMode.DRIVING,
          provideRouteAlternatives: false
        }

        this.directionsService = new window.google.maps.DirectionsService()
        this.directionsDisplay = new window.google.maps.DirectionsRenderer({
          map: this.map,
          suppressMarkers: true,
          preserveViewport: true,
          draggable: false
        })

        this.directionsService.route(directionsConfig, (response, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && response && this.directionsDisplay) {
            this.directionsDisplay.setDirections(response)
          }
        })
      }
    } catch (error) {
      throw new Error(error)
    }
  }

  renderMarkers = function ({ from = null, to = null, adjustMarkerType = 'originDestiny' }) {
    if (!this.changedCoordinates(from, to)) return

    try {
      const positions = []
      const originPosition = from || this.originPosition
      const destinyPosition = to || this.destinyPosition
      this.originPosition = originPosition
      this.destinyPosition = destinyPosition
      this.removeAllMarkers()
      this.removePreviousRoutes()

      if (this.originPosition) positions.push(this.originPosition)
      if (this.destinyPosition) positions.push(this.destinyPosition)

      positions.forEach(pos => this.addMarker({ pos, draggable: true, adjustMarkerType }))
      if (isValidArray(this.markersArray)) this.directions()

      // when active ride call centerMarkerOnMap() method
      if (adjustMarkerType === 'trackRide') this.centerMarkerOnMap(originPosition)
    } catch (error) {
      throw new Error(error)
    }
  }

  changedCoordinates (from, to) {
    const fromLatitude = from && from.latitude ? from.latitude : null
    const fromLongitude = from && from.longitude ? from.longitude : null
    const toLatitude = to && to.latitude ? to.latitude : null
    const toLongitude = to && to.longitude ? to.longitude : null
    const originPositionLatitude = this.originPosition && this.originPosition.latitude ? this.originPosition.latitude : null
    const originPositionLongitude = this.originPosition && this.originPosition.longitude ? this.originPosition.longitude : null
    const destinyPositionLatitude = this.destinyPosition && this.destinyPosition.latitude ? this.destinyPosition.latitude : null
    const destinyPositionLongitude = this.destinyPosition && this.destinyPosition.longitude ? this.destinyPosition.longitude : null

    return fromLatitude !== originPositionLatitude ||
      fromLongitude !== originPositionLongitude ||
      toLatitude !== destinyPositionLatitude ||
      toLongitude !== destinyPositionLongitude
  }

  centerMarkerOnMap (position) {
    this.map.setCenter(new window.google.maps.LatLng(position.latitude, position.longitude))
    this.adjustMapZoom && this.setZoom(18)
    this.adjustMapZoom = false
  }

  async updateMarkerPosition (event, ref) {
    try {
      const latitude = this.getCoordinateValue(event.latLng.lat)
      const longitude = this.getCoordinateValue(event.latLng.lng)
      const address = await this.convertPositionToAddress(`${latitude},${longitude}`)
      const newPosition = {
        ref,
        address: address.formatted_address,
        latitude,
        longitude
      }

      if (ref === 'origin') this.originPosition = newPosition
      if (ref === 'destiny') this.destinyPosition = newPosition
      this.renderMarkers({})

      // propagation event when move map marker
      store.dispatch('webcallMoveMarkers/toggle', !store.state.webcallMoveMarkers.toggle)
    } catch (error) {
      throw new Error(error)
    }
  }

  async renderFavoriteAddress ({ ref, address, number = null, latitude, longitude }) {
    const position = {
      ref,
      address,
      number,
      latitude,
      longitude
    }

    if (ref === 'origin') {
      this.originPosition = position
    }

    if (ref === 'destiny') {
      this.destinyPosition = position
    }

    this.addMarker({
      pos: position,
      draggable: true,
      adjustMarkerType: 'originDestiny'
    })

    if (isArray(this.markersArray) && this.markersArray.length === 1) {
      this.setZoom(18)
    }

    if (isArray(this.markersArray) && this.markersArray.length > 1) {
      this.removePreviousRoutes()
      this.directions()
    }

    // propagation event
    store.dispatch('webcallMoveMarkers/toggle', !store.state.webcallMoveMarkers.toggle)
  }

  async convertAddressToCoordinates (address) {
    try {
      const geocoder = new window.google.maps.Geocoder()

      return new Promise((resolve, reject) => {
        return geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK') {
            const data = isValidArray(results) && results[0].geometry && results[0].geometry.location
              ? {
                latitude: this.getCoordinateValue(results[0].geometry.location.lat),
                longitude: this.getCoordinateValue(results[0].geometry.location.lng)
              }
              : {}
            return resolve(data)
          }

          return reject(status)
        })
      })
    } catch (error) {
      catchError(error)
    }
  }
}

export default GMaps
